import { fetcher, useClientPortalAPI } from "@certn/api";
import { BasicButton, rem, Stack, Text } from "@certn/cascadia";
import { Skeleton } from "@certn/cascadia-react/patterns/skeleton/Skeleton";
import { css } from "@emotion/css";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CredentialCard } from "../../../components/credentials/CredentialCard";
import { UnlockCredentialsModal } from "../../../components/credentials/UnlockCredentialsModal";
import { WithdrawalRequestCard } from "../../../components/credentials/WithdrawalRequestCard";
import { Schemas } from "../../../types";

interface Props {
  caseId: string;
}

export function Credentials({ caseId }: Props) {
  const { data, mutate, isLoading } = useClientPortalAPI(
    `/api/internal/client-portal/case/${caseId}/credential-request/`
  );
  const { t } = useTranslation();
  const [showUnlockModal, setShowUnlockModal] = useState(false);

  const credentialDefinitions = data?.requested_credential_definitions;
  const credentials =
    data?.credentials_data ?? ({} as Schemas["CredentialJson"]);

  const isLocked =
    data?.credentials_data === null && data?.status === "CREDENTIALS_AVAILABLE";
  const isWithdrawalRequested = data?.status === "WITHDRAWAL_REQUESTED";

  const unlockCredential = async () => {
    await fetcher.submit(
      `/api/internal/client-portal/case/${caseId}/credential-request/make-available/`,
      {
        method: "POST",
      }
    );
    mutate();
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <Stack gap="space-2">
          <Skeleton width={200} height={28} />
          <Skeleton width={300} height={20} />
        </Stack>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Stack
        orientation="horizontal"
        align="center"
        justify="space-between"
        gap="space-2"
      >
        <Text style="global/title-small">{t("Credentials")}</Text>
        {isLocked && (
          <BasicButton leftIcon="lock" onPress={() => setShowUnlockModal(true)}>
            {t("Unlock credentials")}
          </BasicButton>
        )}
      </Stack>

      {isWithdrawalRequested && (
        <WithdrawalRequestCard
          caseId={caseId}
          holderName={data?.holder_full_name ?? ""}
          credentials={credentialDefinitions?.map(
            (credentialDefinition) => credentialDefinition.kind
          )}
        />
      )}

      {credentialDefinitions?.map((credentialDefinition) => (
        <CredentialCard
          key={credentialDefinition.kind}
          name={credentialDefinition.kind}
          isLocked={isLocked}
          status={data?.status}
          withdrawnDate={data?.withdrawn_at || undefined}
          sharedDate={data?.shared_at || undefined}
          createdDate={data?.created}
          credentialDefinition={credentialDefinition}
          credential={
            credentials?.[credentialDefinition.type as keyof typeof credentials]
          }
          onUnlock={() => setShowUnlockModal(true)}
        />
      ))}

      <UnlockCredentialsModal
        isOpen={showUnlockModal}
        cost={data?.cost}
        credentials={credentialDefinitions?.map(
          (credentialDefinition) => credentialDefinition.kind
        )}
        onClose={() => setShowUnlockModal(false)}
        onSave={() => {
          unlockCredential();
          setShowUnlockModal(false);
        }}
      />
    </div>
  );
}

const styles = {
  container: css({
    display: "flex",
    flexDirection: "column",
    gap: rem("space-3"),
    paddingRight: rem("space-5"),
    paddingLeft: rem("space-5"),
    paddingTop: rem("space-6"),
    paddingBottom: rem("space-6"),
  }),
  credentialContainer: css({
    padding: rem("space-4"),
  }),
  lockCard: css({
    padding: rem("space-1"),
  }),
  withdrawalRequestCard: css({
    padding: rem("space-4"),
  }),
};
